import {Component, DestroyRef, inject, OnDestroy, OnInit} from '@angular/core';
import {NgxSpinnerComponent, NgxSpinnerService} from 'ngx-spinner';
import {BsAppService, ToastsContainerComponent} from 'bs-components';
import {switchMap} from 'rxjs';
import {filter, tap} from 'rxjs/operators';
import {EventMessageType} from './core/models/event-message-type.enum';
import {LanguagesService} from './core/services/languages.service';
import {SseEventsService} from './core/services/sse-events.service';
import {CurrentUserService} from './core/services/current-user.service';
import {AuthenticationService,} from './core/services/authentication.service';
import packageInfo from '../../package.json';

import {CheckTermsComponent} from './components/check-terms/check-terms.component';
import {HeaderComponent} from './components/header/header.component';
import {ActivationEnd, Router, RouterOutlet} from '@angular/router';
import {EnvironmentService} from './core/services/environment.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [NgxSpinnerComponent, CheckTermsComponent, HeaderComponent, RouterOutlet, ToastsContainerComponent]
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly spinner = inject(NgxSpinnerService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly environmentService = inject(EnvironmentService);
  private readonly sseEvents = inject(SseEventsService);
  private readonly langService = inject(LanguagesService);
  private readonly appService = inject(BsAppService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  public isLoaded: boolean = false;
  public isTermsChecked: boolean = false;

  ngOnInit() {
    this.appService.version$.set(packageInfo.version);
    this.spinner.show().then();
    const userToken = this.authenticationService.getUserToken();
    this.isTermsChecked = userToken.data.termsChecked;

    this.environmentService.environmentChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.sseEvents.startListening();
      });

    this.router.events.pipe(
      filter(event => event instanceof ActivationEnd && !event.snapshot.firstChild),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((event: ActivationEnd) => {
      this.environmentService.isMultiEnvironment.set(!!event.snapshot.data['isMultiEnvironment']);
    });

    this.langService.getAll()
      .pipe(
        switchMap(() => this.currentUserService.getMe(userToken)),
        switchMap(() => this.langService.initUserLanguage())
      )
      .subscribe(() => {
        this.subscribeToLogOutEvent();
        this.isLoaded = true;
        this.spinner.hide();
      });

  }

  ngOnDestroy() {
    this.sseEvents.stopListening();
  }

  private subscribeToLogOutEvent(): void {
    this.sseEvents
      .on(EventMessageType.SSO_LOGOUT_EVENT)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((event) => {
          if (event) {
            this.authenticationService.logout().then();
          }
        })
      )
      .subscribe();

  }


  public receiveIsTermsChecked(checked: boolean): void {
    this.isTermsChecked = checked;
  }
}
